import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import AdminLayout from "./components/layout/AdminLayout";
import BranchDetailPage from "./pages/branches/BranchDetailPage";
import BranchesPage from "./pages/branches/BranchesPage";
import EmployeeContractDetailPage from "./pages/employees/EmployeeContractDetailPage";
import EmployeeContractsPage from "./pages/employees/EmployeeContractsPage";
import EmployeeDetailPage from "./pages/employees/EmployeeDetailPage";
import EmployeePayrollDetailPage from "./pages/employees/EmployeePayrollDetailPage";
import EmployeePayrollsPage from "./pages/employees/EmployeePayrollsPage";
import EmployeesPage from "./pages/employees/EmployeesPage";
import FaqsPage from "./pages/faqs/FaqsPage";
import LoginPage from "./pages/LoginPage";
import MemberDetailPage from "./pages/members/MemberDetailPage";
import MembersListPage from "./pages/members/MembersListPage";
import NoticesPage from "./pages/notices/NoticesPage";
import OwnerCertificationDetailPage from "./pages/owners/OwnerCertificationDetailPage";
import OwnerCertificationsPage from "./pages/owners/OwnerCertificationsPage";
import AppUpdateSettingsPage from "./pages/appUpdate/AppUpdateSettingsPage";
import AppBasicSettingsPage from "./pages/appBasicSettings/AppBasicSettingsPage";
import PoliciesPage from "./pages/policies/PoliciesPage";
import ApiEnvSettingsPage from "./pages/settings/ApiEnvSettingsPage";
import InquiriesPage from "./pages/inquiries/InquiriesPage";
import InquiryDetailPage from "./pages/inquiries/InquiryDetailPage";
import { useAppSelector } from "./store/hooks";

import BranchEmployeesPage from "./pages/branches/BranchEmployeesPage";
import BranchWorkStatusPage from "./pages/branches/BranchWorkStatusPage";
import BranchPayrollsPage from "./pages/branches/BranchPayrollsPage";
import BranchPayrollDetailPage from "./pages/branches/BranchPayrollDetailPage";
import GuardianConsentPage from "./pages/guardianConsent/GuardianConsentPage";

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  return isAuthenticated ? <>{children}</> : <Navigate to="/admin/login" replace />;
};

function App() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/admin/members" replace />} />
      <Route path="/admin/login" element={<LoginPage />} />
      <Route path="/guardian-consent/:linkId" element={<GuardianConsentPage />} />
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        <Route path="members" element={<MembersListPage />} />
        <Route path="members/:userId" element={<MemberDetailPage />} />
        <Route path="branches" element={<BranchesPage />} />
        <Route path="branches/:branchId" element={<BranchDetailPage />} />
        <Route path="branches/:branchId/employees" element={<BranchEmployeesPage />} />
        <Route path="branches/:branchId/work-status" element={<BranchWorkStatusPage />} />
        <Route path="branches/:branchId/payrolls" element={<BranchPayrollsPage />} />
        <Route path="branches/:branchId/payrolls/:payrollId" element={<BranchPayrollDetailPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/:employeeId" element={<EmployeeDetailPage />} />
        <Route path="employees/:employeeId/contracts" element={<EmployeeContractsPage />} />
        <Route path="employees/:employeeId/contracts/:contractId" element={<EmployeeContractDetailPage />} />
        <Route path="employees/:employeeId/payrolls" element={<EmployeePayrollsPage />} />
        <Route path="employees/:employeeId/payrolls/:payrollId" element={<EmployeePayrollDetailPage />} />
        <Route path="owner-certifications" element={<OwnerCertificationsPage />} />
        <Route path="owner-certifications/:branchId" element={<OwnerCertificationDetailPage />} />
        <Route path="notices" element={<NoticesPage />} />
        <Route path="faqs" element={<FaqsPage />} />
        <Route path="inquiries" element={<InquiriesPage />} />
        <Route path="inquiries/:inquiryId" element={<InquiryDetailPage />} />
        <Route path="policies" element={<PoliciesPage />} />
        <Route path="app-basic-settings" element={<AppBasicSettingsPage />} />
        <Route path="app-update" element={<AppUpdateSettingsPage />} />
        <Route path="api-env" element={<ApiEnvSettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/admin/members" replace />} />
    </Routes>
  );
}

export default App;
